import React, { ReactNode, useCallback, useContext } from "react";
import { Badge } from "reactstrap";
import { Link as RouteLink } from "react-router-dom";
import { AppContext } from "../../context/app";
import { checkIfHasAccessToRoute } from "../../routes/util";
import { RouteChildren, Route } from "../../routes/routes-list";

export type SidebarItemProps = Partial<RouteChildren & Route>;

const SidebarItem = ({
  name,
  path: to,
  reactPage,
  icon: Icon,
  badgeColor,
  badgeText,
  target,
  subPaths = [],
}: SidebarItemProps) => {
  const { user, spa } = useContext(AppContext);

  const Link = useCallback(
    ({ children }: { children: ReactNode }) => {
      // If app is inside SPA and next route is an React page we can use NavLink to navigate
      if (spa && reactPage) {
        return (
          <RouteLink to={to} className="sidebar-link">
            {children}
          </RouteLink>
        );
      }

      // If not, it's an embed from PHP, so we should just use regular <a> tag
      return (
        <a href={to} className="sidebar-link active" target={target}>
          {children}
        </a>
      );
    },
    [spa, reactPage, to, target],
  );

  const getSidebarItemClass = useCallback(
    (path) => {
      const isActiveSubPath = subPaths.some(
        (subPath) => window.location.pathname.indexOf(subPath) === 0,
      );
      return window.location.pathname === path || isActiveSubPath
        ? "active"
        : "";
    },
    [subPaths],
  );

  if (!user) return <></>;

  // Check if there is permission to show the sidebar item
  if (!checkIfHasAccessToRoute(user, to)) return <></>;

  return (
    <li className={`sidebar-item ${getSidebarItemClass(to)}`}>
      <Link>
        {Icon && (
          <Icon
            className="align-middle mr-2"
            color="white"
            style={{
              width: "18px",
              height: "18px",
            }}
          />
        )}
        {name}
        {badgeColor && badgeText && (
          <Badge color={badgeColor} size={18} className="sidebar-badge">
            {badgeText}
          </Badge>
        )}
      </Link>
    </li>
  );
};

export default SidebarItem;
